<script lang="ts">
  import CinemaTile from "./cinema-tile/CinemaTile.svelte";
  import { logError } from "../../logger.ts";
  import type {
    TrackingFeature,
    TrackingLabels,
  } from "../lib/tracking/trackingUtils.ts";
  import {
    buildCinemaFeatureForView,
    buildCinemaFeatureForScroll,
    buildTileFeatureForView,
    castTrackingFeaturesToFeatures,
    extractBaseLabels,
  } from "../lib/tracking/trackingUtils.ts";
  import {
    submitEventMerge,
    submitEvent,
    submitMerge,
  } from "@otto-ec/tracking-bct";
  import { setContext } from "svelte";
  import cinemaTilePromiseKey from "../lib/tracking/cinemaTilePromiseKey";
  import type { CinemaSponsoredProduct } from "../types/CinemaProductsDto.types.ts";

  const {
    variations = [] as CinemaSponsoredProduct[],
    onAllProductTilesFailed = () => {},
    auctionId = "",
    cinemaTrackingLabels = {} as TrackingLabels,
    eventMergeId = "",
    useOfcPricingItem = false,
  } = $props();

  const defaultArrowPosition = "50%";

  let cinemaElement = $state<HTMLElement | undefined>();
  let arrowButtonPosition = $state(defaultArrowPosition);
  let failedProductTileCount = 0;

  const mountedCinemaTiles: Promise<void>[] = [];
  setContext<(p: Promise<void>) => void>(cinemaTilePromiseKey, (p) => {
    mountedCinemaTiles.push(p);
  });

  $effect(() => {
    /*               */
    if (eventMergeId) {
      Promise.all(mountedCinemaTiles).then(() => {
        /*                                                                          */
        const baseLabels = extractBaseLabels(getVisibleTileFeatures());
        submitEventMerge(eventMergeId, baseLabels);
      });
    } else {
      /*                          */
      Promise.all(mountedCinemaTiles).then(() => {
        const baseLabels = extractBaseLabels(getVisibleTileFeatures());
        const features = getVisibleTileFeatures();
        submitMerge(baseLabels, castTrackingFeaturesToFeatures(features));
      });
    }
  });

  /*                                                                                 */
  $effect(() => {
    const cinema = cinemaElement;
    if (!cinema) return;

    const updatePosition = () => {
      const productImage = cinema.querySelector<HTMLElement>(
        ".cinema-product-image",
      );

      if (productImage) {
        arrowButtonPosition = `${productImage.offsetTop + productImage.clientHeight / 2}px`;
      }
    };

    /*                                                                                       */
    const observer = new ResizeObserver(updatePosition);
    observer.observe(cinema);

    /*                                                                              */
    return () => observer.disconnect();
  });

  function getVisibleTileFeatures(): TrackingFeature[] {
    const cinemaBounds = cinemaElement!.getBoundingClientRect();
    const visibleTiles = variations
      .map((variation, index) => ({ variation, position: index }))
      .filter(({ position }) => {
        const tile = cinemaElement!.children[position] as
          | HTMLElement
          | undefined;
        if (!tile) return false;
        const tileBounds = tile.getBoundingClientRect();
        return (
          tileBounds.right > cinemaBounds.left &&
          tileBounds.left < cinemaBounds.right
        );
      });
    const variationFeaturesForView = visibleTiles.map(
      ({ variation, position }) =>
      buildTileFeatureForView(
        auctionId,
        variation.variationId,
        position + 1,
        variation.tileTrackingLabels,
      ),
    );

    return [
      buildCinemaFeatureForView(auctionId, cinemaTrackingLabels),
      ...variationFeaturesForView,
    ];
  }

  type CinemaScrollEvent = CustomEvent<{
    visibleItems: { indices: number[] };
    scrollDirection: "right" | "left";
  }>;

  function handleScrollCompleted(event: Event) {
    const scrollEvent = event as CinemaScrollEvent;
    const { visibleItems, scrollDirection } = scrollEvent.detail;
    const actionName = scrollDirection === "right" ? "next" : "previous";

    const visibleVariations: {
      variation: CinemaSponsoredProduct;
      position: number;
    }[] = visibleItems.indices
      .filter((position) => !!variations[position])
      .map((position) => ({
        variation: variations[position] as CinemaSponsoredProduct,
        position,
      }));
    const variationFeaturesForView = visibleVariations.map(
      ({ variation, position }) =>
      buildTileFeatureForView(
        auctionId,
        variation.variationId,
        position + 1,
        variation.tileTrackingLabels,
      ),
    );

    const features: TrackingFeature[] = [
      buildCinemaFeatureForScroll(auctionId, cinemaTrackingLabels),
      ...variationFeaturesForView,
    ];

    const baseLabels = extractBaseLabels(features);
    const convertedFeatures = castTrackingFeaturesToFeatures(features);
    submitEvent?.(baseLabels, {
      features: convertedFeatures,
      name: actionName,
    });
  }
</script>

<oc-cinema-v1
  class="spa-cinema"
  bind:this={cinemaElement}
  oc-aria-label="Gesponserte Produkte"
  arrow-button-position={arrowButtonPosition}
  gap="8px"
  resizing="cut"
  onoc-stage-scroll-completed={handleScrollCompleted}
>
  {#each variations as variation, index}
    <svelte:boundary
      onerror={(error) => {
        logError(
          Error(`CinemaTile ${index} failed to render.`, { cause: error }),
        );
        failedProductTileCount++;
        if (failedProductTileCount >= variations.length)
          onAllProductTilesFailed();
      }}
    >
      <CinemaTile
        {variation}
        lazyLoad={index > 4}
        {auctionId}
        position={index + 1}
        {cinemaTrackingLabels}
        {useOfcPricingItem}
      />
      {#snippet failed()}{/snippet}
    </svelte:boundary>
  {/each}
</oc-cinema-v1>

<style lang="scss">
  .spa-cinema {
    --items-per-page: 2;
  }

  /*                                                                           */
  @container (width > 767px) {
    .spa-cinema {
      --items-per-page: 5;
    }
  }

  @container ((width > 636px) and (width <= 767px)) {
    .spa-cinema {
      --items-per-page: 4;
    }
  }

  @container ((width >= 497px) and (width <= 636px)) {
    .spa-cinema {
      --items-per-page: 3;
    }
  }

  @container (width < 497px) {
    .spa-cinema {
      --items-per-page: 2;
    }
  }
</style>
